'use client';

import type { SwiperOptions } from 'swiper/types';
import type {
  BenefitQuickCategoryGroupType,
  BenefitQuickCategoryItemType,
  BenefitQuickCategoryWidgetData,
} from '@weberus/shared-types';
import { useEffect, type FC } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { waitUntilClickTimeout } from '@weberus/utils';
import { useInView } from 'react-intersection-observer';
import { clientLogProtobuf } from '@/app/_shared/utils/client-log';
import { cn } from '@/app/_shared/utils/tw-cn';
import { navigateTo } from '@/app/_shared/utils/action-navigator';
import { HomePageLog } from '@/app/_shared/protobuf-log/clientlogs/home-common-txtpb';
import { BaseImage } from '@/app/_components/common/base-image';

export interface BenefitQuickCategoryProps {
  data: BenefitQuickCategoryWidgetData;
}

export interface BenefitQuickCategoryItemProps extends BenefitQuickCategoryItemType {
  isFirst: boolean;
  isLast: boolean;
}

export const swiperConfig: SwiperOptions = {
  slidesPerView: 'auto',
  threshold: 10,
  centeredSlides: false,
};

export const BenefitQuickCategoryItem: FC<BenefitQuickCategoryItemProps> = ({
  title,
  image,
  action,
  serverLogMeta,
  isFirst,
  isLast,
}) => {
  const { ref, inView } = useInView({ triggerOnce: true });
  const logMeta: HomePageLog.QuickCategoryBoardRowItem.Impression = {
    widgetPosition: serverLogMeta.widgetPosition,
    position: serverLogMeta.position,
    logMeta: serverLogMeta,
  };

  const handleClick = async (ev: React.MouseEvent<HTMLAnchorElement>) => {
    ev.preventDefault();
    // eslint-disable-next-line @typescript-eslint/no-confusing-void-expression
    await waitUntilClickTimeout(clientLogProtobuf(HomePageLog.QuickCategoryBoardRowItem.click, logMeta)());
    navigateTo(action);
  };

  useEffect(() => {
    if (inView) {
      clientLogProtobuf(HomePageLog.QuickCategoryBoardRowItem.impression, logMeta)();
    }
  }, [inView, serverLogMeta]);

  return (
    <a
      ref={ref}
      href={action.web}
      className={cn(
        'flex h-40 flex-1 items-center justify-center bg-fill-neutral-weak pc:h-72 pc:rounded-16 pc:first:rounded-16 pc:last:rounded-16',
        {
          'rounded-l-8': isFirst,
          'rounded-r-8': isLast,
        },
      )}
      onClick={handleClick}>
      <span className="relative mr-2 size-18 pc:mr-6 pc:size-24">
        <BaseImage alt="" className="size-full" src={image.url} />
      </span>
      <span className="typography-body-12-regular-fixed pc:typography-subtitle-18-regular-fixed">{title}</span>
    </a>
  );
};

export const BenefitQuickCategorySwiper: FC<BenefitQuickCategoryGroupType> = (group) => {
  return (
    <Swiper {...swiperConfig} wrapperClass="flex gap-1">
      {group.items.slice(0, group.max).map((item, idx) => (
        <SwiperSlide key={idx} className="max-w-108 [&:first-child]:ml-16 [&:last-child]:mr-16">
          <BenefitQuickCategoryItem {...item} isFirst={idx === 0} isLast={idx === group.items.length - 1} />
        </SwiperSlide>
      ))}
    </Swiper>
  );
};

export const BenefitQuickCategoryFlex: FC<BenefitQuickCategoryGroupType> = (group) => {
  return (
    <div className="flex w-full flex-row items-center space-x-1 pc:space-x-4">
      {group.items.slice(0, group.max).map((item, idx) => (
        <BenefitQuickCategoryItem {...item} key={idx} isFirst={idx === 0} isLast={idx === group.items.length - 1} />
      ))}
    </div>
  );
};

/**
 *  위젯_퀵카테고리(혜택)
 *
 *  모바일 환경에서 아이템이 4개일 때 swiper 로 노출
 *
 *  @see {@link https://www.figma.com/design/zlHuiOHmYv1ghVDbTHUSvW/%5BGUIDE%5D-%EC%9C%84%EC%A0%AF%2F%EC%98%A4%EB%8D%94%EB%A7%81?node-id=7-5951&m=dev}
 */
export const BenefitQuickCategory: FC<BenefitQuickCategoryProps> = ({ data }) => {
  const group = data.items?.[0];

  if (!group) {
    return null;
  }

  const isSwiperEnabled = group.count >= group.max;

  return (
    <>
      <div className={cn('my-12 hidden w-full pc:w-pc-size', { 'block pc:hidden': isSwiperEnabled })}>
        <BenefitQuickCategorySwiper {...group} />
      </div>
      <div className={cn('w-full pc:my-16 pc:w-pc-size', { 'hidden pc:block': isSwiperEnabled })}>
        <BenefitQuickCategoryFlex {...group} />
      </div>
    </>
  );
};
